import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BigQueryUsageStat, UsagePeriod } from 'app/interfaces/bigquery-usage.interface';
import { Database } from 'app/interfaces/database.interface';
import { QueryExecutionLog } from 'app/interfaces/query-execution-log.interface';
import { QueryLogEntry } from 'app/interfaces/query-log-entry.interface';
import { QueryExecutionResult } from 'app/interfaces/query-execution-result.interface';
import { QueryFilter } from 'app/interfaces/query-filter.interface';
import { Query } from 'app/interfaces/query.interface';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class QueryService {
  private http = inject(HttpClient);

  private baseUrl = `${environment.apiUrl}`;

  private _tags$ = new BehaviorSubject<string[]>([]);
  readonly tags$ = this._tags$.asObservable();

  currentFilter$ = new BehaviorSubject<QueryFilter>({
    name: null,
    database_id: null,
    creator_email: null,
    tags: [],
    table_name: null,
    favorites_only: false,
    sort_by: 'name',
    show_active: true,
    show_deleted: false,
    only_private: false,
  });

  getQueries(filter?: Partial<QueryFilter>): Observable<Query[]> {
    let params = new HttpParams();
    const f = filter ?? this.currentFilter$.value;
    if (f.name) {
      params = params.set('name', f.name);
    }
    if (f.database_id) {
      params = params.set('database_id', f.database_id);
    }
    if (f.creator_email) {
      params = params.set('creator_email', f.creator_email);
    }
    if (f.tags?.length) {
      f.tags.forEach((t) => (params = params.append('tags[]', t)));
    }
    if (f.table_name) {
      params = params.set('table_name', f.table_name);
    }
    if (f.favorites_only) {
      params = params.set('favorites_only', '1');
    }
    if (f.sort_by) {
      params = params.set('sort_by', f.sort_by);
    }
    if (f.only_private) {
      params = params.set('only_private', '1');
    }
    if (!f.show_active && f.show_deleted) {
      params = params.set('only_deleted', '1');
    } else if (f.show_active && f.show_deleted) {
      params = params.set('with_deleted', '1');
    }
    return this.http.get<Query[]>(`${this.baseUrl}/queries`, { params });
  }

  getQuery(id: string): Observable<Query> {
    return this.http.get<Query>(`${this.baseUrl}/queries/${id}`);
  }

  createQuery(data: Partial<Query>): Observable<Query> {
    return this.http.post<Query>(`${this.baseUrl}/queries`, data);
  }

  updateQuery(id: string, data: Partial<Query>): Observable<Query> {
    return this.http.put<Query>(`${this.baseUrl}/queries/${id}`, data);
  }

  deleteQuery(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/queries/${id}`);
  }

  restoreQuery(id: string): Observable<Query> {
    return this.http.post<Query>(`${this.baseUrl}/queries/${id}/restore`, {});
  }

  duplicateQuery(id: string, name: string): Observable<Query> {
    return this.http.post<Query>(`${this.baseUrl}/queries/${id}/duplicate`, { name });
  }

  toggleFavorite(id: string): Observable<{ is_favorite: boolean }> {
    return this.http.post<{ is_favorite: boolean }>(`${this.baseUrl}/queries/${id}/favorite`, {});
  }

  executeQuery(
    id: string,
    values: Record<string, string | null>,
    format: 'html' | 'csv' | 'json' = 'html'
  ): Observable<QueryExecutionResult> {
    return this.http.post<QueryExecutionResult>(`${this.baseUrl}/queries/${id}/execute`, { values, format });
  }

  exportQueryToCsv(id: string, values: Record<string, string | null>): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${this.baseUrl}/queries/${id}/export`, { values });
  }

  estimateQuery(id: string, values: Record<string, string | null>): Observable<{ bytes_processed: number }> {
    return this.http.post<{ bytes_processed: number }>(`${this.baseUrl}/queries/${id}/estimate`, { values });
  }

  getLogs(id: string): Observable<QueryLogEntry[]> {
    return this.http.get<QueryLogEntry[]>(`${this.baseUrl}/queries/${id}/logs`);
  }

  getExecutions(id: string): Observable<QueryExecutionLog[]> {
    return this.http.get<QueryExecutionLog[]>(`${this.baseUrl}/queries/${id}/executions`);
  }

  getBigQueryUsage(period: UsagePeriod): Observable<BigQueryUsageStat[]> {
    return this.http.get<BigQueryUsageStat[]>(`${this.baseUrl}/bigquery-usage`, {
      params: { period },
    });
  }

  getDatabases(): Observable<Database[]> {
    return this.http.get<Database[]>(`${this.baseUrl}/databases`);
  }

  getCreators(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/queries/creators`);
  }

  getTags(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/queries/tags`);
  }

  refreshTags(): void {
    this.getTags()
      .pipe(tap((tags) => this._tags$.next(tags)))
      .subscribe();
  }

  applyFilter(filter: QueryFilter): void {
    this.currentFilter$.next(filter);
  }
}
